import { AnyExtension, Extension } from "@tiptap/core";
import Document from "@tiptap/extension-document";
import HardBreak from "@tiptap/extension-hard-break";
import Paragraph from "@tiptap/extension-paragraph";
import Placeholder from "@tiptap/extension-placeholder";
import Text from "@tiptap/extension-text";
import { Editor, EditorContent, useEditor } from "@tiptap/react";
import { compact } from "lodash";
import { useEffect, useMemo, useRef } from "react";

import { classNames } from "@helpers/css";

const TextOnlyWysiwyg = ({
  deps = [],
  value,
  editable = false,
  autofocus = false,
  placeholder = "",
  className = "",
  extraExtensions,
  onChangeValue,
  onEnterPressed,
  onEditorReady,
}: {
  deps?: any[];
  value?: string | null;
  autofocus?: boolean;
  editable?: boolean;
  placeholder?: string;
  className?: string;
  extraExtensions?: AnyExtension[];
  onChangeValue?: (value: string) => void;
  onEnterPressed?: () => void;
  onEditorReady?: (editor: Editor) => void;
}) => {
  const content = !value // if falsy, return null
    ? null
    : value; // or we just return the string value.

  const onEnterPressedRef = useRef(onEnterPressed);
  onEnterPressedRef.current = onEnterPressed;

  const enterSubmitExtension = useMemo(
    () =>
      Extension.create({
        name: "textOnlyEnterSubmit",
        addKeyboardShortcuts() {
          return {
            Enter: () => {
              onEnterPressedRef.current?.();
              return true;
            },
          };
        },
      }),
    [],
  );

  const editor = useEditor(
    {
      injectNonce: window.TF?.cspNonce,
      // Keep the Tiptap 2 default: re-render on every transaction.
      shouldRerenderOnTransaction: true,
      editable,
      autofocus,
      extensions: compact([
        Text,
        Paragraph,
        HardBreak.configure({ keepMarks: false }),
        Document,
        Placeholder.configure({
          placeholder: placeholder,
          showOnlyCurrent: true,
        }),
        ...(extraExtensions ?? []),
        enterSubmitExtension,
      ]),
      editorProps: {
        attributes: {
          class: classNames(className, "no-placeholder-on-every-line"),
        },
      },
      content,
    },
    [...deps, extraExtensions],
  );

  // `useEditor` bakes editorProps in when the editor is created, so a caller
  // that changes `className` between renders (e.g. the Ask AI input switching
  // between its one-line and full styles) would never see the new class. Push it
  // onto the live editor instead — this keeps the same DOM node, so focus and
  // CSS transitions survive.
  useEffect(() => {
    if (!editor) return;
    editor.setOptions({
      editorProps: {
        attributes: {
          class: classNames(className, "no-placeholder-on-every-line"),
        },
      },
    });
  }, [editor, className]);

  useEffect(() => {
    const handleUpdateValue = ({ editor }: any) => {
      if (onChangeValue) onChangeValue(editor.getText());
    };
    editor?.on("update", handleUpdateValue);
    return function cleanup() {
      editor?.off("update", handleUpdateValue);
    };
  }, [editor, onChangeValue]);

  useEffect(() => {
    if (onEditorReady && editor) onEditorReady(editor);
  }, [editor, onEditorReady]);

  return (
    <>
      <EditorContent editor={editor} className="flex-1 min-w-0" />
    </>
  );
};

export default TextOnlyWysiwyg;
